import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getTags,
  getTag,
  createTag,
  updateTag,
  deleteTag,
  type Tag,
  type CreateTagRequest,
  type UpdateTagRequest,
} from "@/services/api/tags";

const tagsKeys = {
  all: ["tags"] as const,
  lists: () => [...tagsKeys.all, "list"] as const,
  list: (filters?: Record<string, unknown>) =>
    [...tagsKeys.lists(), { filters }] as const,
  details: () => [...tagsKeys.all, "detail"] as const,
  detail: (id: string | number) => [...tagsKeys.details(), id] as const,
};

export function useTags(
  options?: Omit<UseQueryOptions<Tag[], Error>, "queryKey" | "queryFn">
) {
  return useQuery<Tag[], Error>({
    queryKey: tagsKeys.list(),
    queryFn: getTags,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useTag(
  id: string | number,
  options?: Omit<UseQueryOptions<Tag, Error>, "queryKey" | "queryFn">
) {
  return useQuery<Tag, Error>({
    queryKey: tagsKeys.detail(id),
    queryFn: () => getTag(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useCreateTag() {
  const queryClient = useQueryClient();

  return useMutation<Tag, Error, CreateTagRequest>({
    mutationFn: createTag,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: tagsKeys.lists() });
      toast.success("Tag created", {
        description: `"${data.name ?? ""}" has been successfully created.`,
      });
    },
    onError: (error) => {
      toast.error("Failed to create tag", {
        description: error.message || "An error occurred while creating tag.",
      });
    },
  });
}

export function useUpdateTag() {
  const queryClient = useQueryClient();

  return useMutation<
    Tag,
    Error,
    { id: string | number; data: UpdateTagRequest }
  >({
    mutationFn: ({ id, data }) => updateTag(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: tagsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tagsKeys.detail(data.id) });
      toast.success("Tag updated", {
        description: `"${data.name ?? ""}" has been successfully updated.`,
      });
    },
    onError: (error) => {
      toast.error("Failed to update tag", {
        description: error.message || "An error occurred while updating tag.",
      });
    },
  });
}

export function useDeleteTag() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string | number>({
    mutationFn: deleteTag,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagsKeys.lists() });
      toast.success("Tag deleted", {
        description: "The tag has been successfully deleted.",
      });
    },
    onError: (error) => {
      toast.error("Failed to delete tag", {
        description: error.message || "An error occurred while deleting tag.",
      });
    },
  });
}

export { tagsKeys };
